<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import Blok from '../../blok.svelte';
	import type { ImageBlock } from '../imageBlock.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import type { SbImage, SbLink } from '$lib/storyblok/types';
	import Image from '$lib/storyblok/components/image.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import ArrowRight from '$lib/icons/arrowRight.svelte';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			description: ISbRichtext;
			image: Array<ISbStoryData<ImageBlock>>;
			partner_name: string;
			logo: SbImage;
			partner_link_label: string;
			partner_link: SbLink;
		}>['content'];
	}

	let { content }: Props = $props();
</script>

<section class="bg-white py-8 lg:pb-12 lg:pt-32" use:editable={content}>
	<PageGrid16 margin>
		<div class="col-span-full flex flex-col items-start lg:col-start-2 lg:col-end-[16]">
			<div
				class="mb-5 lg:mb-10 lg:flex lg:w-full lg:flex-row-reverse lg:items-center lg:justify-between"
			>
				<div class="pb-[17px] pr-1 pt-[11px] max-lg:mb-3">
					<Image
						src={content.logo.filename}
						alt={content.logo.alt}
						title={content.logo.title}
						class="h-13 lg:h-23"
					/>
				</div>

				<h1
					class="col-span-full text-balance text-[36px]/[112%] tracking-[-.36px] text-brown-1 lg:w-1/2 lg:text-[48px]/[120%] lg:tracking-[-.48px]"
				>
					{content.headline}
				</h1>
			</div>

			{#if content.image.length > 0}
				<div class="col-span-full aspect-2/1 w-full">
					<Blok content={content.image[0]} />
				</div>
			{/if}

			<div
				class="mb-3 mt-5 flex items-center gap-x-2 text-[12px]/[160%] text-dark-grey lg:mt-13 lg:text-[16px]/[150%]"
			>
				<span>Meet</span>
				<span class="h-[1px] w-8 bg-dark-grey"></span>
				<span>{content.partner_name}</span>
			</div>

			<div class="text-[14px]/[160%] text-dark-grey lg:text-[32px]/[150%] lg:tracking-[-.32px]">
				<SbRichtext text={content.description} />
			</div>

			{#if content.partner_link}
				<Button
					class="btn-brown mt-13"
					href={getSbUrl(content.partner_link)}
					target="_blank"
					rel="noopener noreferrer"
				>
					<span>{content.partner_link_label}</span>
					<ArrowRight />
				</Button>
			{/if}
		</div>
	</PageGrid16>
</section>
